<template>
  <div class="settings-page">
    <div class="container-fluid py-1">
      <div class="row">
        <div class="col-12">
          <div class="card my-4">
            <!-- Settings Header -->
            <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
              <div
                class="bg-gradient-primary shadow-primary border-radius-lg pt-2 pb-3"
              >
                <h6 class="text-white text-capitalize ps-3">System Settings</h6>
                <p class="text-white text-sm ps-3 mb-0">
                  Manage API tokens and environment settings
                </p>
              </div>
            </div>

            <div class="card-body p-4">
              <!-- Main Tabs - Only Tokens -->
              <ul class="nav nav-tabs mb-1" id="mainTabs" role="tablist">
                <li class="nav-item" role="presentation">
                  <button class="nav-link active" @click="activeTab = 'tokens'">
                    <i class="fas fa-key me-2"></i>
                    API Tokens
                  </button>
                </li>
              </ul>

              <!-- Tokens Tab Content -->
              <div class="tab-content">
                <div class="row">
                  <!-- NCEMS Token Card -->
                  <div class="col-md-6 mb-4">
                    <div class="card h-100">
                      <div class="card-header">
                        <h5 class="mb-0">
                          <i
                            class="fas fa-external-link-alt me-2 text-primary"
                          ></i>
                          NCEMS Access Token
                        </h5>
                        <p class="text-sm mb-0">
                          Paste your NCEMS system access token
                        </p>
                      </div>
                      <div class="card-body">
                        <div class="mb-3">
                          <label class="form-label">NCEMS Token</label>
                          <div class="input-group">
                            <input
                              class="form-control"
                              :type="showNcemsToken ? 'text' : 'password'"
                              v-model="ncems_token"
                              placeholder="Paste NCEMS access token here"
                              :disabled="isSavingToken"
                            />
                            <button
                              class="btn btn-outline-secondary"
                              type="button"
                              @click="showNcemsToken = !showNcemsToken"
                            >
                              <i
                                :class="
                                  showNcemsToken
                                    ? 'fas fa-eye-slash'
                                    : 'fas fa-eye'
                                "
                              ></i>
                            </button>
                          </div>
                          <div class="form-text">
                            This token is generated in the sCore CCS Back Office
                            Operator Portal.
                          </div>
                        </div>

                        <div class="alert alert-info">
                          <div class="d-flex align-items-center">
                            <i class="fas fa-info-circle me-2"></i>
                            <div class="text-sm">
                              <strong>Note:</strong> The NCEMS token will be
                              securely stored and hidden from view after saving.
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="card-footer">
                        <button
                          class="btn btn-primary w-100"
                          @click="saveNcemsToken"
                          :disabled="!ncems_token || isSavingToken"
                        >
                          <i class="fas fa-save me-2"></i>
                          {{ isSavingToken ? "Saving..." : "Save NCEMS Token" }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- App Token Card -->
                  <div class="col-md-6 mb-4">
                    <div class="card h-100">
                      <div class="card-header">
                        <h5 class="mb-0">
                          <i class="fas fa-key me-2 text-success"></i>
                          Application Token
                        </h5>
                        <p class="text-sm mb-0">
                          Generate and manage your application token
                        </p>
                      </div>
                      <div class="card-body">
                        <!-- App Token Display -->
                        <div v-if="app_token && showAppToken" class="mb-4">
                          <label class="form-label">Generated App Token</label>
                          <div class="alert alert-light border border-success">
                            <div
                              class="d-flex justify-content-between align-items-center"
                            >
                              <code class="text-break app-token-display">{{
                                app_token
                              }}</code>
                              <button
                                class="btn btn-sm btn-outline-success ms-2"
                                @click="copyAppToken"
                                title="Copy to clipboard"
                              >
                                <i class="fas fa-copy"></i>
                              </button>
                            </div>
                            <!-- <div class="mt-2 text-xs text-muted">
                              <i class="fas fa-clock me-1"></i>
                              Generated: {{ formatDate(date_generated) }}
                            </div> -->
                          </div>
                        </div>

                        <!-- Token Status -->
                        <div v-else-if="app_token" class="mb-4">
                          <label class="form-label">App Token Status</label>
                          <div class="alert alert-info">
                            <div class="d-flex align-items-center">
                              <i class="fas fa-eye-slash me-2"></i>
                              <div>
                                <strong>Token is hidden</strong>
                                <!-- <div class="text-xs">
                                  Generated on
                                  {{ formatDate(date_generated) }}
                                </div> -->
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- No Token Generated -->
                        <div v-else class="mb-4">
                          <label class="form-label">App Token Status</label>
                          <div class="alert alert-light border">
                            <div class="d-flex align-items-center">
                              <i class="fas fa-key me-2 text-muted"></i>
                              <div>
                                <div class="text-xs">
                                  Generate a new application token to enable API
                                  access
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Token Actions -->
                        <div class="mt-4">
                          <h6 class="mb-3">Token Actions</h6>
                          <div class="d-grid gap-2">
                            <button
                              v-if="!app_token"
                              class="btn btn-success"
                              @click="generateAppToken"
                              :disabled="isGeneratingToken"
                            >
                              <i class="fas fa-plus-circle me-2"></i>
                              {{
                                isGeneratingToken
                                  ? "Generating..."
                                  : "Generate New App Token"
                              }}
                            </button>

                            <button
                              v-if="app_token && !showAppToken"
                              class="btn btn-info"
                              @click="showAppToken = true"
                            >
                              <i class="fas fa-eye me-2"></i>
                              View App Token
                            </button>

                            <button
                              v-if="app_token && showAppToken"
                              class="btn btn-outline-secondary"
                              @click="showAppToken = false"
                            >
                              <i class="fas fa-eye-slash me-2"></i>
                              Hide App Token
                            </button>

                            <button
                              v-if="app_token"
                              class="btn btn-outline-warning"
                              @click="confirmRegenerateToken"
                              :disabled="isGeneratingToken"
                            >
                              <i class="fas fa-redo me-2"></i>
                              Regenerate App Token
                            </button>
                          </div>
                        </div>

                        <!-- Token Information -->
                        <div class="mt-4 p-3 bg-light border-radius-lg">
                          <h6 class="mb-2">
                            <i class="fas fa-info-circle me-2 text-primary"></i>
                            Token Information:
                          </h6>
                          <ul class="text-sm mb-0">
                            <li>App Token is used for API authentication</li>
                            <li>You can view and copy your token anytime</li>
                            <li>You can regenerate the token at any time</li>
                            <li>Both tokens are stored securely</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Token Summary 
                <div class="row mt-2">
                  <div class="col-12">
                    <div class="card">
                      <div class="card-header">
                        <h6 class="mb-0">
                          <i class="fas fa-clipboard-check me-2"></i>
                          Token Status Summary
                        </h6>
                      </div>
                      <div class="card-body">
                        <div class="row">
                          <div class="col-md-4">
                            <div class="d-flex align-items-center mb-1">
                              <div
                                class="icon icon-shape icon-sm bg-gradient-info me-3"
                              >
                                <i class="fas fa-external-link-alt"></i>
                              </div>
                              <div>
                                <h6 class="mb-0">NCEMS Token</h6>
                                <p class="text-sm text-secondary mb-0">
                                  Status:
                                  <span
                                    :class="
                                      ncems_token
                                        ? 'text-success'
                                        : 'text-warning'
                                    "
                                  >
                                    <strong>{{
                                      ncems_token
                                        ? "Configured"
                                        : "Not Configured"
                                    }}</strong>
                                  </span>
                                </p>
                              </div>
                            </div>
                          </div>
                          <div class="col-md-4">
                            <div class="d-flex align-items-center mb-3">
                              <div
                                class="icon icon-shape icon-sm bg-gradient-success me-3"
                              >
                                <i class="fas fa-key"></i>
                              </div>
                              <div>
                                <h6 class="mb-0">App Token</h6>
                                <p class="text-sm text-secondary mb-0">
                                  <span v-if="date_generated" class="d-block">
                                    Last generated:
                                    <strong>
                                      {{ formatDate(date_generated) }}</strong
                                    >
                                  </span>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>-->
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
export default {
  name: "SystemSettings",
  data() {
    return {
      activeTab: "tokens",

      // Tokens Data
      ncems_token: "",
      showNcemsToken: false,
      isSavingToken: false,

      app_token: "",
      showAppToken: false,
      isGeneratingToken: false,
      date_generated: null,
    };
  },
  methods: {
    async fetchAppToken() {
      try {
        const response = await axios.get("/get-app-token/");
        if (response.data.app_token) {
          this.app_token = response.data.app_token;
        }
      } catch (error) {
        console.error("Error fetching app token:", error);
      }
    },

    async saveNcemsToken() {
      if (!this.ncems_token.trim()) {
        this.showNotification("Please enter NCEMS token", "warning");
        return;
      }

      this.isSavingToken = true;
      try {
        await axios.post("/save-ncems-token/", {
          ncems_token: this.ncems_token,
        });

        this.showNotification("NCEMS token saved successfully!", "success");
        this.showNcemsToken = false;
      } catch (error) {
        console.error("Error saving NCEMS token:");
        this.showNotification("Failed to save NCEMS token. Please try again.");
      } finally {
        this.isSavingToken = false;
      }
    },

    async generateAppToken() {
      this.isGeneratingToken = true;
      try {
        const response = await axios.post("/create-app-token/");

        this.app_token = response.data.app_token;
        this.date_generated = new Date();
        this.showAppToken = true;

        this.showNotification("App token generated successfully!", "success");
      } catch (error) {
        console.error("Error generating app token");
        this.showNotification(
          "Failed to generate app token. Please try again.",
        );
      } finally {
        this.isGeneratingToken = false;
      }
    },

    confirmRegenerateToken() {
      if (
        confirm(
          "Are you sure you want to regenerate the app token? The old token will become invalid.",
        )
      ) {
        this.generateAppToken();
      }
    },

    copyAppToken() {
      navigator.clipboard
        .writeText(this.app_token)
        .then(() => {
          this.showNotification("Token copied to clipboard!", "success");
        })
        .catch((err) => {
          console.error("Failed to copy token:", err);
          this.showNotification("Failed to copy token", "error");
        });
    },

    // Helper Methods
    showNotification(message, type = "success") {
      // Remove any existing notifications
      const existingAlerts = document.querySelectorAll(".fixed-notification");
      existingAlerts.forEach((alert) => alert.remove());

      const notification = document.createElement("div");

      // Base classes
      notification.className =
        "alert alert-dismissible fade show fixed-notification";

      // Set styles based on type
      if (type === "success") {
        notification.style.background =
          "linear-gradient(310deg, #4caf50 0%, #8bc34a 100%)";
        notification.style.color = "white";
        notification.style.border = "none";
        notification.style.boxShadow =
          "0 4px 20px 0 rgba(76, 175, 80, 0.14), 0 7px 10px -5px rgba(76, 175, 80, 0.4)";
      } else if (type === "error") {
        notification.style.background =
          "linear-gradient(310deg, #f44336 0%, #f88a82 100%)";
        notification.style.color = "white";
        notification.style.border = "none";
        notification.style.boxShadow =
          "0 4px 20px 0 rgba(244, 67, 54, 0.14), 0 7px 10px -5px rgba(244, 67, 54, 0.4)";
      }

      // Position styles
      notification.style.position = "fixed";
      notification.style.top = "20px";
      notification.style.right = "20px";
      notification.style.zIndex = "9999";
      notification.style.minWidth = "300px";

      // Create content
      const strong = document.createElement("strong");
      strong.textContent = type === "error" ? "Error! " : "Success! ";
      notification.appendChild(strong);

      const textNode = document.createTextNode(message);
      notification.appendChild(textNode);

      const closeButton = document.createElement("button");
      closeButton.type = "button";
      closeButton.className = "btn-close";
      closeButton.style.filter = "brightness(0) invert(1)";
      closeButton.setAttribute("data-bs-dismiss", "alert");
      closeButton.setAttribute("aria-label", "Close");

      notification.appendChild(closeButton);
      document.body.appendChild(notification);

      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
      }, 3000);
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  watch: {
    activeTab() {
      // Hide app token when switching tabs
      this.showAppToken = false;
      this.showNcemsToken = false;
    },
  },
  mounted() {
    this.fetchAppToken();
  },
};
</script>

<style scoped>
.settings-page {
  min-height: 100vh;
}

.icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 0.5rem;
  color: white;
}

.bg-gradient-primary {
  background: linear-gradient(310deg, #f44335 0%, #ef5350 100%);
}

.bg-gradient-info {
  background: linear-gradient(310deg, #17a2b8 0%, #3dcfd3 100%);
}

.bg-gradient-success {
  background: linear-gradient(310deg, #4caf50 0%, #8bc34a 100%);
}

.bg-gradient-warning {
  background: linear-gradient(310deg, #ff9800 0%, #ffb74d 100%);
}

/* Main Tabs */
#mainTabs .nav-link {
  color: #6c757d;
  font-weight: 600;
  border: none;
  padding: 1rem 1.5rem;
  transition: all 0.3s ease;
}

#mainTabs .nav-link.active {
  color: #4caf50;
  border-bottom: 3px solid #4caf50;
  background-color: transparent;
}

#mainTabs .nav-link:hover:not(.active) {
  color: #4caf50;
  background-color: rgba(76, 175, 80, 0.05);
}

/* Environment Switch */
.form-check-input:checked {
  background-color: #4caf50;
  border-color: #4caf50;
}

.form-check-input:focus {
  border-color: #4caf50;
  box-shadow: 0 0 0 0.2rem rgba(76, 175, 80, 0.25);
}

.form-check-label {
  font-weight: 500;
  color: #344767;
}

/* Token Display - Black Text */
.app-token-display {
  color: #000 !important;
  font-weight: 500;
  background: rgba(0, 0, 0, 0.05) !important;
  padding: 0.5rem !important;
  border-radius: 0.375rem !important;
}

/* Token Cards */
.card {
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* FIX FOR INPUT FIELDS - ENSURE VISIBILITY */
.form-control {
  display: block !important;
  width: 100% !important;
  padding: 0.75rem 1rem !important;
  font-size: 1rem !important;
  font-weight: 400 !important;
  line-height: 1.5 !important;
  color: #495057 !important;
  background-color: #fff !important;
  background-clip: padding-box !important;
  border: 1px solid #d2d6da !important;
  border-radius: 0.5rem !important;
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out !important;
  height: 48px !important;
  min-height: 48px !important;
  max-height: 48px !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.form-control:focus {
  color: #495057 !important;
  background-color: #fff !important;
  border-color: #4caf50 !important;
  outline: 0 !important;
  box-shadow: 0 0 0 0.2rem rgba(76, 175, 80, 0.25) !important;
}

.form-control:disabled {
  background-color: #e9ecef !important;
  opacity: 0.6 !important;
}

.input-group {
  position: relative !important;
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: stretch !important;
  width: 100% !important;
}

.input-group > .form-control {
  position: relative !important;
  flex: 1 1 auto !important;
  width: 1% !important;
  min-width: 0 !important;
  margin-bottom: 0 !important;
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  border-right: none !important;
}

.input-group > .btn {
  position: relative !important;
  z-index: 2 !important;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  height: 48px !important;
  min-height: 48px !important;
  max-height: 48px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0.75rem 1rem !important;
}

.form-label {
  display: inline-block !important;
  margin-bottom: 0.5rem !important;
  font-weight: 500 !important;
  color: #344767 !important;
}

.form-text {
  margin-top: 0.25rem !important;
  font-size: 0.875rem !important;
  color: #6c757d !important;
}

/* Ensure input group buttons are visible */
.btn-outline-secondary {
  color: #6c757d !important;
  border-color: #d2d6da !important;
  background-color: #fff !important;
}

.btn-outline-secondary:hover {
  color: #fff !important;
  background-color: #6c757d !important;
  border-color: #6c757d !important;
}

/* Button styling */
.btn-info {
  background-color: #17a2b8 !important;
  border-color: #17a2b8 !important;
  color: white !important;
}

.btn-info:hover {
  background-color: #138496 !important;
  border-color: #117a8b !important;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .nav-link {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
  }

  .d-grid.gap-2 {
    gap: 0.5rem !important;
  }

  .environment-switch {
    margin-top: 1rem;
    text-align: center;
  }
}
</style>
